import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ImportBatchDto, ImportKind, ImportPreviewResult } from "@grizzly/shared";
import { api } from "../lib/api.js";
import type { ApiClientError } from "../lib/http.js";
import { useToast, type ToastApi } from "../lib/useToast.js";
import { useI18n, type I18nApi, type MessageKey } from "../i18n/index.js";

const KIND_OPTIONS: { value: ImportKind; labelKey: MessageKey }[] = [
  { value: "customers", labelKey: "imports.kind.customers" },
  { value: "suppliers", labelKey: "imports.kind.suppliers" },
  { value: "materials", labelKey: "imports.kind.materials" },
  { value: "products", labelKey: "imports.kind.products" },
  { value: "boms", labelKey: "imports.kind.boms" },
  { value: "routings", labelKey: "imports.kind.routings" },
  { value: "opening-stock", labelKey: "imports.kind.opening-stock" },
];

export { KIND_OPTIONS };

function applyCommitError(
  err: unknown,
  kind: ImportKind,
  fileName: string,
  preview: ImportPreviewResult | null,
  push: ToastApi["push"],
  setPreview: Dispatch<SetStateAction<ImportPreviewResult | null>>,
  t: I18nApi["t"]
): void {
  const message = (err as ApiClientError).message;
  const details = (err as ApiClientError).details as
    { issues?: { row: number; message: string }[] } | undefined;
  if (!details?.issues?.length) {
    push(message, undefined, "danger");
    return;
  }
  push(
    t("imports.commitIssues", { message, count: details.issues.length }),
    undefined,
    "danger"
  );
  setPreview({
    kind,
    fileName,
    totalRows: preview?.totalRows ?? 0,
    okRows: 0,
    issues: details.issues,
    headers: preview?.headers ?? [],
  });
}

export function useImports() {
  const { push } = useToast();
  const { t } = useI18n();
  const [kind, setKind] = useState<ImportKind>("customers");
  const [fileName, setFileName] = useState("");
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [batches, setBatches] = useState<ImportBatchDto[]>([]);
  const [busy, setBusy] = useState(false);

  const loadBatches = useCallback(async () => {
    const res = await api.listBatches();
    setBatches(res.items);
  }, []);

  useEffect(() => {
    void loadBatches().catch(() => {});
  }, [loadBatches]);

  const runPreview = useCallback(async () => {
    setBusy(true);
    try {
      const result = await api.preview(kind, fileName, csv);
      setPreview(result);
      if (result.issues.length === 0) {
        push(t("imports.previewOk", { count: result.okRows }));
      } else {
        push(
          t("imports.rowIssuesFound", { count: result.issues.length }),
          undefined,
          "danger"
        );
      }
    } catch (err) {
      push((err as ApiClientError).message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [kind, fileName, csv, push, t]);

  const runCommit = useCallback(async () => {
    setBusy(true);
    try {
      const result = await api.commit(kind, fileName, csv);
      push(t("imports.importedRows", { count: result.inserted }));
      setPreview(null);
      setCsv("");
      setFileName("");
      await loadBatches();
    } catch (err) {
      applyCommitError(err, kind, fileName, preview, push, setPreview, t);
    } finally {
      setBusy(false);
    }
  }, [kind, fileName, csv, push, loadBatches, preview, t]);

  return {
    kind,
    setKind,
    fileName,
    setFileName,
    csv,
    setCsv,
    preview,
    batches,
    busy,
    runPreview,
    runCommit,
  };
}
